/**
 * Aura AGM chatflow (SG · Aura Partners). Studio: title → 회사(L_ENTITY) 선택 → confirm + live package.
 * 문서 필드 ✨ → Contact 조회, 📅 날짜, 📍 주소.
 */
import type { ConversationMessage } from '@/features/AI-documents/types/docGen.types';
import type {
  ChatFlow,
  ChatFlowContext,
  ChatFlowEventResult,
} from '@/features/startTask/preset_task/shared/preset/chatflows/types';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';

export const auraAgmSoleCorpFlow: ChatFlow = {
  id: 'aura-agm-sole-corp',
  taskCodeMatch: ['CAU004'],
  categoryMatch: ['aura agm', 'agm', 'annual general meeting'],

  getInitialMessages(preSet: PreSetSelection): ConversationMessage[] {
    const docCount = preSet.documents.length;
    return [
      {
        role: 'ai',
        content:
          `Welcome to **Aura AGM** (Audited · Sole Member — Corporate).\n\n` +
          `This preset prepares a live document package` +
          (docCount > 0 ? ` (${docCount} template(s) linked)` : '') +
          `: Letter of Notification & Lodgement of AR, AGM Documents (DRW / Notice of AGM / Consent to Short Notice / Minutes s.179(6)), ` +
          `DDF (per director), and a Notice for Controller (individual / corporate).\n\n` +
          `First, enter a **Task Title**, then select the **company**.`,
        timestamp: Date.now(),
        stepId: 1,
        source: 'system',
      },
    ];
  },

  steps: [
    {
      id: 'input-title',
      aiMessage: 'Please enter a Task Title.',
      widget: 'text-input',
      widgetConfig: { targetVarKey: '_taskTitle', targetEntityIndex: 0 },
      awaitEvent: true,
    },
    {
      id: 'select-entity',
      aiMessage: 'Search and select the **company** for this AGM, or **skip** to fill the documents manually.',
      widget: 'entity-selector',
      // 엔티티 없이도 진행 — "Skip — fill documents manually" 버튼(entity-skip) 노출
      widgetConfig: { targetVarKey: '_entity', targetEntityIndex: 0, allowSkip: true },
      awaitEvent: true,
    },
    {
      id: 'confirm',
      aiMessage: 'Complete the AGM documents on the right, then click **Generate**.',
      widget: null,
      nextStep: null,
    },
    { id: 'officer-role', aiMessage: 'Select a role to look up.', widget: 'officer-role-buttons', awaitEvent: true },
    { id: 'officer-list', aiMessage: 'Select a person from the list.', widget: 'officer-list', awaitEvent: true },
    // ⚠ officer-field-picker + awaitEvent 여야 chatBridge 가 상세 fetch → selectedPerson 저장 →
    //   패널이 활성 필드에 자동 채운다. widget:null 이면 자동채움이 안 걸린다(레퍼런스와 동일해야 함).
    { id: 'officer-detail', aiMessage: 'Person details loaded. The field has been pre-filled — you can also drag/click the values below.', widget: 'officer-field-picker', awaitEvent: true },
    { id: 'address-input', aiMessage: 'Enter the address below.', widget: 'address-input', awaitEvent: true },
  ],

  handleEvent(eventName: string, payload: any, context: ChatFlowContext): ChatFlowEventResult {
    if (
      (eventName === 'value-submit' || eventName === 'text-input-submit') &&
      context.currentStepId === 'input-title'
    ) {
      const title = typeof payload === 'string' ? payload : payload?.value || payload;
      if (!title || !String(title).trim()) return {};
      return {
        messages: [{ role: 'user', content: String(title), timestamp: Date.now(), stepId: 1 }],
        formUpdates: { _taskTitle: String(title).trim(), taskTitle: String(title).trim() },
        nextStepId: 'select-entity',
      };
    }

    if (eventName === 'entity-select' && context.currentStepId === 'select-entity') {
      const ent = payload || {};
      const entityCd = ent.entityCd || ent.icpCd || '';
      const entityName = ent.entityName || ent.companyName || 'Entity';
      if (!entityCd) return {};
      return {
        messages: [{ role: 'user', content: `Selected: **${entityName}**`, timestamp: Date.now(), stepId: 1 }],
        formUpdates: {
          entityCd,
          icpCd: entityCd,
          entityName,
          company_name: entityName,
          registrationNo: ent.registrationNo || ent.regNo || '',
        },
        nextStepId: 'confirm',
      };
    }

    if (eventName === 'entity-skip' || (eventName === 'skip' && context.currentStepId === 'select-entity')) {
      return {
        messages: [{ role: 'user', content: 'Skipped — fill manually.', timestamp: Date.now(), stepId: 1 }],
        formUpdates: { entityCd: '', icpCd: '', entitySkipped: true },
        nextStepId: 'confirm',
      };
    }

    if (eventName === 'request-person-lookup') {
      const ctx = payload || {};
      const key = String(ctx.key || '');
      return {
        messages: [{ role: 'user', content: `Looking up person for **${key || 'field'}**…`, timestamp: Date.now(), stepId: 1 }],
        formUpdates: { _personTargetKey: key },
        nextStepId: ctx.defaultRole ? 'officer-list' : 'officer-role',
        nextStepWidgetData: {
          selectedRole: ctx.defaultRole || undefined,
          entityCd: String(ctx.defaultRole || '').toUpperCase() === 'CONTACT' ? '' : (ctx.entityCd || context.formData?.entityCd || ''),
        } as any,
      };
    }

    if (eventName === 'officer-role-select') {
      const role = typeof payload === 'string' ? payload : payload?.role || payload?.value;
      if (!role) return {};
      return {
        messages: [{ role: 'user', content: `Role: **${role}**`, timestamp: Date.now(), stepId: 1 }],
        nextStepId: 'officer-list',
        nextStepWidgetData: {
          selectedRole: role,
          entityCd: String(role || '').toUpperCase() === 'CONTACT' ? '' : (context.formData?.entityCd || ''),
        } as any,
      };
    }

    if (eventName === 'officer-select' || eventName === 'person-select') {
      return {
        messages: [{ role: 'user', content: 'Person selected.', timestamp: Date.now(), stepId: 1 }],
        nextStepId: 'officer-detail',
        formUpdates: { _selectedOfficer: payload },
      };
    }

    if (eventName === 'request-address-input') {
      const key = String(payload?.key || payload || '').trim();
      return {
        messages: [{ role: 'user', content: key ? `Enter address for **${key}**.` : 'Enter an address.', timestamp: Date.now(), stepId: 1 }],
        formUpdates: { _addressTargetKey: key },
        nextStepId: 'address-input',
      };
    }

    if (eventName === 'entity-field-select' && context.currentStepId === 'address-input') {
      return {
        messages: [{ role: 'user', content: 'Applied to the document.', timestamp: Date.now(), stepId: 1 }],
        nextStepId: 'confirm',
      };
    }

    return {};
  },
};
